import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateParentChild, type ParentChild } from "../../../services/api";
import { uploadProfileImage } from "../../../services/users";
import { GameButton } from "../../../components/game";
import DateInput from "../../../components/DateInput";
import { normalizeImageForUpload } from "../../../lib/imageFile";

/**
 * One editable child card for the parent Settings screen. A parent can change
 * the child's photo, name, birth date, login identifier (username / email) and
 * reset the child's password. Saves via PATCH /parent/children/:childId.
 */
export default function ChildSettingsEditor({
  child,
  onSaved,
}: {
  child: ParentChild;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const a = child.athlete;
  const fileRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [avatar, setAvatar] = useState<string | null>(a.profile_url || null);

  const [form, setForm] = useState({
    full_name: a.full_name || "",
    date_of_birth: a.date_of_birth ? String(a.date_of_birth).slice(0, 10) : "",
    // The child's login identifier. `email` may be a synthetic address for
    // username-only accounts; we surface both fields and let the parent edit.
    email: a.email && !a.email.endsWith("@no-email.invalid") ? a.email : "",
  });
  const [newPassword, setNewPassword] = useState("");

  const inputClass =
    "w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";

  const flash = (setter: (v: string) => void, msg: string) => {
    setter(msg);
    setTimeout(() => setter(""), 3000);
  };

  const handleAvatar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0];
    if (!picked) return;
    // No file.type gate — Android's picker often reports no MIME type for an
    // ordinary photo. The file is normalised below (HEIC → JPEG, downscaled)
    // and the server validates the real bytes.
    try {
      setUploading(true);
      setError("");
      const file = await normalizeImageForUpload(picked);
      if (file.size > 5 * 1024 * 1024) {
        flash(setError, t("profile.imageTooLarge", "Imaginea este prea mare. Maxim 5MB."));
        return;
      }
      const { url } = await uploadProfileImage(file);
      await updateParentChild(child.athlete_id, { profile_url: url });
      setAvatar(url);
      flash(setSuccess, t("childSettings.saved", "Modificările au fost salvate."));
      onSaved();
    } catch (err: any) {
      flash(setError, err?.response?.data?.error || t("profile.imageUploadError", "Nu s-a putut încărca imaginea"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setError("");
    setSuccess("");
    if (!form.full_name.trim()) {
      flash(setError, t("childSettings.nameRequired", "Numele este obligatoriu."));
      return;
    }
    if (newPassword && newPassword.length < 6) {
      flash(setError, t("profile.passwordTooShort", "Parola trebuie să aibă minim 6 caractere"));
      return;
    }
    setSaving(true);
    try {
      await updateParentChild(child.athlete_id, {
        full_name: form.full_name.trim(),
        date_of_birth: form.date_of_birth || null,
        email: form.email.trim() || undefined,
        password: newPassword || undefined,
      });
      setNewPassword("");
      flash(setSuccess, t("childSettings.saved", "Modificările au fost salvate."));
      onSaved();
    } catch (err: any) {
      flash(setError, err?.response?.data?.error || t("childSettings.saveError", "Nu s-au putut salva modificările."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-50 rounded-2xl p-4">
      <div className="flex items-center gap-4">
        <div className="relative w-14 h-14 shrink-0">
          {avatar ? (
            <img src={avatar} alt={a.full_name} className="w-14 h-14 rounded-full object-cover border-2 border-white shadow" />
          ) : (
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white text-lg font-extrabold">
              {a.full_name?.charAt(0) || "?"}
            </div>
          )}
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="absolute -bottom-1 -right-1 w-7 h-7 bg-indigo-500 rounded-full flex items-center justify-center shadow hover:bg-indigo-600 active:translate-y-[1px]"
            aria-label={t("childSettings.changePhoto", "Schimbă poza")}
          >
            {uploading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatar} className="hidden" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-extrabold text-slate-800 truncate">{a.full_name}</h3>
          {a.athlete_profiles?.club && (
            <p className="text-xs text-slate-400 truncate">{a.athlete_profiles.club.name}</p>
          )}
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          className="text-sm font-bold text-indigo-600 shrink-0"
        >
          {open ? t("childSettings.close", "Închide") : t("childSettings.edit", "Editează")}
        </button>
      </div>

      {open && (
        <div className="mt-4 pt-4 border-t border-slate-200 space-y-3">
          {error && <div className="p-2.5 bg-red-50 text-red-600 rounded-xl text-sm font-semibold">{error}</div>}
          {success && <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-xl text-sm font-semibold">{success}</div>}

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("profile.fullName", "Nume complet")}</label>
            <input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className={inputClass} />
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("profile.dateOfBirth", "Data nașterii")}</label>
            <DateInput value={form.date_of_birth} onChange={(iso) => setForm({ ...form, date_of_birth: iso })} className={inputClass} />
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("profile.email", "Email")}</label>
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} placeholder="email@exemplu.ro" />
            {a.email && a.email.endsWith("@no-email.invalid") && (
              <p className="text-xs text-slate-400 mt-1">
                {t("childSettings.usernameNote", "Copilul se autentifică cu username: {{u}}", { u: a.email.split("@")[0] })}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-700 mb-1.5">{t("childSettings.newPassword", "Parolă nouă (opțional)")}</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputClass} placeholder="••••••" autoComplete="new-password" />
          </div>

          <GameButton type="button" variant="primary" disabled={saving} onClick={save}>
            {saving ? t("common.saving", "Se salvează...") : t("profile.save", "Salvează modificările")}
          </GameButton>
        </div>
      )}
    </div>
  );
}
